import Button from "devextreme-react/button";
import NumberBox from "devextreme-react/number-box";
import SelectBox from "devextreme-react/select-box";
import RadioGroup from "devextreme-react/radio-group";
import Sortable from "devextreme-react/sortable";
import { confirm } from "devextreme/ui/dialog";
import TagBox from "devextreme-react/tag-box";
import TextBox from "devextreme-react/text-box";
import React, { useEffect, useMemo, useState } from "react";
import { DefaultPhaseDto } from "../../../dtos/DefaultPhaseDto";
import { DefaultStepDto } from "../../../dtos/DefaultStepDto";
import { WorkflowConfigOptionsDto } from "../../../dtos/WorkflowConfigOptionsDto";
import { StepFile } from "../../../dtos/StepFile";
import { StepSettings } from "../../../dtos/StepSettings";
import TemplateGroupBox, { removedTextStyle } from "./components/TemplateGroupBox";
import useStagedRows from "./hooks/useStagedRows";

// Which file configuration this tab is editing:
// - "subtask": the subtask's own document slots and upload rules
// - "defaultSubtask": the task's defaults for new subtasks (settings.defaultStepSettings)
// The task's own upload behavior is not here: it is set once on the Add Default Task dialog,
// because moving files after the fact would put SharePoint and the database out of step.
export type FilesMode = "subtask" | "defaultSubtask";

type Props = {
    task?: DefaultPhaseDto | null;
    subtask?: DefaultStepDto | null;
    onSave: (data: DefaultPhaseDto | DefaultStepDto) => Promise<void>;
    mode?: FilesMode;
    // False for a subtask that has separate Request and Response areas: a document names one file,
    // and there is no way to say which of the two areas it belongs to. Such a subtask still gets
    // the upload limit and the "accepts anything" rule, so the tab stays - only the documents go.
    allowConfiguredDocuments?: boolean;
    configOptions?: WorkflowConfigOptionsDto;
};

// The two ways a step can take files. A choice rather than a checkbox, so the documents below
// read as inactive rather than gone when the other one is picked. "Unrestricted" is the word the
// setting itself uses (StepSettings.UnrestrictedUpload), so the screen and the config agree.
const LIMIT_MODES = [
    { id: "none", name: "No limit" },
    { id: "count", name: "Up to" },
];

const UPLOAD_MODES = [
    { id: "any", name: "Unrestricted uploads" },
    { id: "documents", name: "Configured documents" },
];

// The types an admin picks from. The box accepts anything typed in as well, so this is a
// shortcut rather than a whitelist.
const COMMON_EXTENSIONS = [
    "pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx",
    "csv", "txt", "zip", "jpg", "jpeg", "png", "dwg", "rvt"
];

// Editable row model. `key` is what the staged add/remove rows are tracked by, so a row keeps its
// identity through a reorder (its position is not stable, its key is).
type SlotRow = {
    key: string;
    label: string;
    rename: string;
    extensions: string[];
};

let nextRowKey = 0;
const newRowKey = () => `slot-${nextRowKey++}`;

// Normalizes a typed-in extension: "  .PDF " -> "pdf". Empty once trimmed means drop it.
export const normalizeExtension = (value: string): string =>
    value.trim().replace(/^\./, "").toLowerCase();

export const parseExtensions = (values: string[]): string[] => {
    const seen: string[] = [];
    for (const value of values) {
        const ext = normalizeExtension(value);
        if (ext.length > 0 && !seen.includes(ext)) seen.push(ext);
    }
    return seen;
};

// Moving an item from one position to another. Kept out of the component because a drag cannot be
// simulated in jsdom, and this is the part worth testing.
export const reorder = <T,>(items: T[], from: number, to: number): T[] => {
    if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return items;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
};


export const sameExtensions = (a: string[], b: string[]): boolean =>
    a.length === b.length && a.every((value, index) => value === b[index]);

const toRow = (slot: StepFile): SlotRow => ({
    key: newRowKey(),
    label: slot.label ?? "",
    rename: slot.rename ?? "",
    extensions: parseExtensions(slot.allowedExtensions ?? []),
});

export const toSlot = (row: SlotRow): StepFile => {
    const extensions = parseExtensions(row.extensions);
    return {
        label: row.label.trim(),
        rename: row.rename.trim() || null,
        allowedExtensions: extensions.length > 0 ? extensions : null,
    };
};

// The problems that make a save wrong rather than just empty. Blank rows are dropped on save, so
// they are not errors; two slots sharing a label or a rename are, because uploads resolve by
// label and files are stored under the rename.
export const validateSlots = (rows: SlotRow[]): string[] => {
    const errors: string[] = [];
    const filled = rows.filter(r => r.label.trim().length > 0);

    const duplicate = (values: string[]) => {
        const seen = new Set<string>();
        const dupes = new Set<string>();
        for (const value of values) {
            if (seen.has(value)) dupes.add(value);
            seen.add(value);
        }
        return [...dupes];
    };

    for (const label of duplicate(filled.map(r => r.label.trim().toLowerCase()))) {
        errors.push(`Two documents are named "${label}". Each document needs its own name.`);
    }

    const renames = filled.map(r => r.rename.trim().toLowerCase()).filter(r => r.length > 0);
    for (const rename of duplicate(renames)) {
        errors.push(`Two documents both save as "${rename}". Each needs its own file name.`);
    }

    if (rows.some(r => r.label.trim().length === 0 && (r.rename.trim().length > 0 || r.extensions.length > 0))) {
        errors.push("A document needs a name before it can be saved.");
    }

    return errors;
};

export default function TemplateFilesTab({ task, subtask, onSave, mode, allowConfiguredDocuments = true, configOptions }: Props) {
    const effectiveMode: FilesMode = mode ?? (task ? "defaultSubtask" : "subtask");
    const isDefaultSubtaskMode = effectiveMode === "defaultSubtask";

    // Step-shaped settings come from the subtask itself, or from the task's new-subtask defaults.
    const stepSettings = isDefaultSubtaskMode ? task?.settings?.defaultStepSettings : subtask?.settings;

    const buildRows = () => (stepSettings?.fileRestrictions ?? []).map(toRow);

    // The types this subtask takes. A subtask that has none of its own falls back to the seeded
    // list the config options carry, so the rule in force is on screen rather than implied
    // (ticket 1067). An empty list means the subtask takes any type.
    const seededTypes = configOptions?.subtask?.defaultFileSettings?.allowedExtensions ?? [];
    const buildTypes = (settings: StepSettings | null | undefined) =>
        parseExtensions(settings?.allowedExtensions ?? seededTypes);

    const [unrestricted, setUnrestricted] = useState(stepSettings?.unrestrictedUpload ?? false);
    const [fileLimit, setFileLimit] = useState<number | null>(stepSettings?.fileLimit ?? null);
    const [allowedTypes, setAllowedTypes] = useState<string[]>(() => buildTypes(stepSettings));
    const [rows, setRows] = useState<SlotRow[]>(buildRows);

    const staged = useStagedRows();
    // Documents added by hand in this session. Nothing can have been uploaded for one yet, so
    // removing it needs no warning. Restored defaults are NOT in here: a restored document can
    // easily match files that are already in SharePoint under its saved-as name.
    const [newRowKeys, setNewRowKeys] = useState<string[]>([]);

    // Compared as text rather than by field so a reorder counts as a change too.
    const snapshot = (
        u: boolean, limit: number | null, types: string[], slots: SlotRow[]
    ) => JSON.stringify([u, limit, types, slots.map(s => [s.label, s.rename, s.extensions])]);

    const [saved, setSaved] = useState(() => snapshot(
        stepSettings?.unrestrictedUpload ?? false,
        stepSettings?.fileLimit ?? null,
        buildTypes(stepSettings),
        buildRows()
    ));

    // With documents unavailable there is no second mode to choose between, so the subtask accepts
    // anything and the choice is not offered. Saving follows this rather than the hidden radio, so
    // a save also clears a document set left over from before the subtask changed type.
    const acceptsAnything = allowConfiguredDocuments ? unrestricted : true;

    const keptRows = useMemo(() => rows.filter(r => !staged.isRemoved(r.key)), [rows, staged]);
    const current = snapshot(unrestricted, fileLimit, allowedTypes, keptRows);
    const isDirty = current !== saved;

    const errors = useMemo(() => validateSlots(keptRows), [keptRows]);

    // Reset when a different task or subtask is selected.
    useEffect(() => {
        const s = isDefaultSubtaskMode ? task?.settings?.defaultStepSettings : subtask?.settings;
        const initialRows = (s?.fileRestrictions ?? []).map(toRow);

        setUnrestricted(s?.unrestrictedUpload ?? false);
        setFileLimit(s?.fileLimit ?? null);
        setAllowedTypes(buildTypes(s));
        setRows(initialRows);
        setNewRowKeys([]);
        setSaved(snapshot(
            s?.unrestrictedUpload ?? false,
            s?.fileLimit ?? null,
            buildTypes(s),
            initialRows
        ));
        staged.clear();
    }, [task?.id, subtask?.id, effectiveMode]);

    const [collapsed, setCollapsed] = useState<string[]>([]);
    const toggleGroup = (name: string) =>
        setCollapsed(prev => prev.includes(name) ? prev.filter(g => g !== name) : [...prev, name]);

    const updateRow = (key: string, patch: Partial<SlotRow>) =>
        setRows(prev => prev.map(r => (r.key === key ? { ...r, ...patch } : r)));

    // Removing a document only stops new uploads: whatever was uploaded under it is already in
    // SharePoint under its saved-as name and stays there, so the click is worth a question. A row
    // added in this session has nothing behind it yet, so it just goes.
    const confirmRemoveRow = async (row: SlotRow) => {
        if (newRowKeys.includes(row.key)) {
            staged.removeRow(row.key);
            return;
        }

        const name = row.label.trim() || "this document";
        const savedAs = row.rename.trim();
        const confirmed = await confirm(
            `Remove "${name}"? Files already uploaded for it stay in SharePoint` +
            `${savedAs ? ` under the name "${savedAs}"` : ""}, with nothing in the tasklist pointing at them.`,
            "Remove Document"
        );
        if (confirmed) staged.removeRow(row.key);
    };

    const addRow = () => {
        const row = { key: newRowKey(), label: "", rename: "", extensions: [] as string[] };
        setRows(prev => [...prev, row]);
        staged.addRows([row.key]);
        setNewRowKeys(prev => [...prev, row.key]);
    };

    // Dragging works on what is on screen, struck-through rows included - they stay in place, and
    // the save drops them either way.
    const onReorder = (from: number, to: number) => setRows(prev => reorder(prev, from, to));

    const handleSave = async () => {
        if (errors.length > 0) return;

        {
            // The documents are kept whichever mode is picked. Accepting any file makes the
            // backend ignore them, so there is no reason to throw the setup away and no way to
            // lose it by picking the other mode for a moment.
            const slots = keptRows.filter(r => r.label.trim().length > 0).map(toSlot);
            const limit = fileLimit && fileLimit > 0 ? fileLimit : null;
            const restrictions = allowConfiguredDocuments && slots.length > 0 ? slots : null;
            const types = parseExtensions(allowedTypes);

            // The file-related slice of StepSettings, laid over whatever the settings already hold.
            // Spelled out because the generated StepSettings type has no optional members.
            const fileSettings = (current: StepSettings | null | undefined): StepSettings => ({
                unrestrictedUpload: acceptsAnything,
                fileLimit: limit,
                fileRestrictions: restrictions,
                allowedExtensions: types,
                offsetStart: current?.offsetStart ?? null,
                offsetEnd: current?.offsetEnd ?? null,
                dueDate: current?.dueDate ?? null,
                requestFileAttachments: current?.requestFileAttachments ?? null,
                triggerActions: current?.triggerActions ?? null,
                initialStepStatus: current?.initialStepStatus ?? null,
                notifications: current?.notifications ?? null,
                workflow: current?.workflow ?? null,
                workflowType: current?.workflowType ?? null,
                isCore: current?.isCore ?? false,
            });

            if (isDefaultSubtaskMode && task) {
                const updated: DefaultPhaseDto = {
                    ...task,
                    settings: {
                        ...task.settings,
                        defaultStepSettings: fileSettings(task.settings?.defaultStepSettings),
                    },
                };
                await onSave(updated);
            } else if (subtask) {
                const updated: DefaultStepDto = {
                    ...subtask,
                    settings: fileSettings(subtask.settings),
                };
                await onSave(updated);
            }
        }

        setRows(keptRows);
        setNewRowKeys([]);
        setSaved(current);
        staged.clear();
    };

    // What this subtask started life with: the documents its seeded version carries, or the plain
    // "takes anything" default for one nobody seeded.
    const fileDefaults =
        (subtask?.id ? configOptions?.subtask?.defaultFileSettingsOverrides?.[subtask.id] : null)
        ?? configOptions?.subtask?.defaultFileSettings
        ?? null;

    const handleRestoreDefaults = () => {
        const defaultRows = (fileDefaults?.fileRestrictions ?? []).map(toRow);
        setUnrestricted(fileDefaults?.unrestrictedUpload ?? true);
        setFileLimit(fileDefaults?.fileLimit ?? null);
        setAllowedTypes(parseExtensions(fileDefaults?.allowedExtensions ?? seededTypes));
        setRows(defaultRows);
        // The restored rows are new to the form, so their saved-as names stay editable until saved
        // and removing one costs no warning.
        staged.clear();
        staged.addRows(defaultRows.map(r => r.key));
        setNewRowKeys([]);
    };

    const handleDiscard = () => {
        const s = stepSettings;
        setUnrestricted(s?.unrestrictedUpload ?? false);
        setFileLimit(s?.fileLimit ?? null);
        setAllowedTypes(buildTypes(s));
        setRows((s?.fileRestrictions ?? []).map(toRow));
        setNewRowKeys([]);
        staged.clear();
    };

    const typesSummary = allowedTypes.length === 0
        ? "any file type"
        : allowedTypes.join(", ");

    const documentCount = `${keptRows.length} document${keptRows.length === 1 ? "" : "s"}`;
    const slotSummary = unrestricted ? `${documentCount}, not in use` : documentCount;

    return (
        <div className="template-tab">
            <div className="template-tab-body">
                <div style={{ display: "grid", gap: "16px" }}>
                    <p style={{ color: "#666", fontSize: "13px", margin: 0 }}>
                        {isDefaultSubtaskMode
                            ? "What a subtask added to this task starts with. Changing it does not touch subtasks that already exist."
                            : "The documents a user uploads into this subtask. Each is restricted to the types listed and saved under its file name."}
                    </p>

                    <TemplateGroupBox
                        title="Upload rules"
                        summary={
                            acceptsAnything
                                ? `Unrestricted, ${fileLimit ? `at most ${fileLimit}` : "no limit"}, ${typesSummary}`
                                : "One file per configured document"
                        }
                        collapsed={collapsed.includes("rules")}
                        onToggle={() => toggleGroup("rules")}
                        testId="upload-rules-group"
                    >
                        <div style={{ display: "grid", gap: "12px" }}>
                            {allowConfiguredDocuments ? (
                                <RadioGroup
                                    items={UPLOAD_MODES}
                                    displayExpr="name"
                                    valueExpr="id"
                                    layout="horizontal"
                                    value={unrestricted ? "any" : "documents"}
                                    onValueChanged={(e) => setUnrestricted(e.value === "any")}
                                    elementAttr={{ "data-testid": "unrestricted-upload" }}
                                />
                            ) : (
                                <span style={{ fontSize: "13px", color: "#666" }} data-testid="documents-unavailable">
                                    This subtask takes files in a Request and a Response, so uploads
                                    are unrestricted - a configured document could not say which of
                                    the two it belongs to.
                                </span>
                            )}
                            {/* Stays put whichever mode is picked, inert in the other one, the
                                same way the documents below dim rather than disappear. The count is
                                its own choice rather than "empty means no limit", so getting back to
                                no limit is a click instead of knowing to clear the box. */}
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", opacity: acceptsAnything ? 1 : 0.55 }}>
                                <div style={{ display: "grid", gap: "2px", flex: "0 0 30%" }}>
                                    <label style={{ fontWeight: 500 }}>Maximum Uploads</label>
                                    <span style={{ fontSize: "12px", color: "#888" }}>
                                        {!acceptsAnything
                                            ? "Not in use with configured documents - the list below is the count."
                                            : fileLimit === null
                                                ? "Any number of files can be uploaded."
                                                : "Uploads are refused once the subtask holds this many."}
                                    </span>
                                </div>
                                <SelectBox
                                    items={LIMIT_MODES}
                                    displayExpr="name"
                                    valueExpr="id"
                                    value={fileLimit === null ? "none" : "count"}
                                    onValueChanged={(e) => setFileLimit(e.value === "none" ? null : (fileLimit ?? 1))}
                                    width={130}
                                    disabled={!acceptsAnything}
                                    elementAttr={{ "data-testid": "file-limit-mode" }}
                                />
                                <NumberBox
                                    value={fileLimit ?? undefined}
                                    onValueChanged={(e) => setFileLimit(e.value ?? null)}
                                    min={1}
                                    showSpinButtons={true}
                                    width={100}
                                    disabled={!acceptsAnything || fileLimit === null}
                                    visible={fileLimit !== null}
                                    elementAttr={{ "data-testid": "file-limit" }}
                                />
                            </div>

                            {/* The types an unrestricted subtask takes (ticket 1067). Configured
                                documents carry their own types, so this dims with the limit above
                                rather than disappearing. */}
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", opacity: acceptsAnything ? 1 : 0.55 }}>
                                <div style={{ display: "grid", gap: "2px", flex: "0 0 30%" }}>
                                    <label style={{ fontWeight: 500 }}>Allowed types/extensions</label>
                                    <span style={{ fontSize: "12px", color: "#888" }}>
                                        {!acceptsAnything
                                            ? "Not in use with configured documents - each names its own types."
                                            : allowedTypes.length === 0
                                                ? "Any file type can be uploaded."
                                                : "Anything else is refused, on the screen and on the server."}
                                    </span>
                                </div>
                                {/* Fills the rest of the row and wraps its tags onto another line
                                    once they no longer fit, rather than scrolling sideways. */}
                                <div style={{ flex: 1, maxWidth: "70%", minWidth: 0 }}>
                                    <TagBox
                                        items={COMMON_EXTENSIONS}
                                        value={allowedTypes}
                                        // Taken as it comes and only when it differs, for the reason
                                        // the document rows below give: normalizing on the way out
                                        // hands the box a value it does not recognize as its own and
                                        // the two keep re-triggering each other. Normalizing happens
                                        // on save.
                                        onValueChanged={(e) => {
                                            const next: string[] = e.value ?? [];
                                            if (sameExtensions(next, allowedTypes)) return;
                                            setAllowedTypes(next);
                                        }}
                                        // Ticked from the list the same way the documents below are:
                                        // checkboxes keep it open so several types are one visit,
                                        // with the Select All hidden by the CSS (ticking every type
                                        // would read as a restriction to exactly those, when "any
                                        // type" is what an empty box already means). Anything not on
                                        // the list can still be typed in.
                                        dropDownOptions={{ wrapperAttr: { class: "slot-types-dropdown" } }}
                                        showSelectionControls={true}
                                        applyValueMode="instantly"
                                        hideSelectedItems={false}
                                        searchEnabled={true}
                                        acceptCustomValue={true}
                                        onCustomItemCreating={(e) => {
                                            const ext = normalizeExtension(e.text ?? "");
                                            e.customItem = ext.length > 0 ? ext : null;
                                        }}
                                        placeholder="Any file type"
                                        disabled={!acceptsAnything}
                                        width="100%"
                                        elementAttr={{ "data-testid": "allowed-types" }}
                                    />
                                </div>
                            </div>
                        </div>
                    </TemplateGroupBox>

                    {allowConfiguredDocuments && (
                    <TemplateGroupBox
                            title="Documents"
                            summary={slotSummary}
                            collapsed={collapsed.includes("slots")}
                            onToggle={() => toggleGroup("slots")}
                            testId="slot-editor"
                            headerRight={
                                <Button
                                    text="Add document"
                                    icon="add"
                                    stylingMode="outlined"
                                    onClick={addRow}
                                    disabled={unrestricted}
                                    elementAttr={{ "data-testid": "add-slot" }}
                                />
                            }
                        >
                            <div style={{ display: "grid", gap: "8px", opacity: unrestricted ? 0.55 : 1 }}>
                                {unrestricted && (
                                    <div data-testid="documents-inactive" style={{ fontSize: "12px", color: "#888" }}>
                                        Not in use while uploads are unrestricted. What is set up here is kept, so
                                        switching back brings it all with it.
                                    </div>
                                )}
                                <div style={{ display: "flex", gap: "8px", fontWeight: 600, fontSize: "12px", color: "#666" }}>
                                    <div style={{ width: "16px" }} />
                                    <div style={{ flex: 2 }}>Document name/description</div>
                                    <div style={{ flex: 1 }}>Saved as</div>
                                    <div style={{ flex: 1 }}>Allowed types/extensions</div>
                                    <div style={{ width: "40px" }} />
                                </div>

                                {rows.length === 0 && (
                                    <div style={{ color: "#999" }}>
                                        No documents configured. Uploads are unchecked until one is added.
                                    </div>
                                )}

                                <Sortable
                                    itemOrientation="vertical"
                                    handle=".slot-drag-handle"
                                    allowReordering={!unrestricted}
                                    onReorder={(e) => onReorder(e.fromIndex, e.toIndex)}
                                >
                                {rows.map((row) => {
                                    const removed = staged.isRemoved(row.key);
                                    return (
                                        <div
                                            key={row.key}
                                            data-testid="slot-row"
                                            data-removed={removed}
                                            style={{ display: "flex", gap: "8px", alignItems: "center", padding: "6px 0" }}
                                        >
                                            <i
                                                className="dx-icon dx-icon-dragvertical slot-drag-handle"
                                                title="Drag to reorder"
                                                style={{ cursor: unrestricted ? "default" : "grab", color: "#aaa", fontSize: "16px" }}
                                            />
                                            <div style={{ flex: 2 }}>
                                                <TextBox
                                                    value={row.label}
                                                    onValueChanged={(e) => updateRow(row.key, { label: e.value })}
                                                    placeholder="e.g. Drawings"
                                                    width="100%"
                                                    disabled={removed || unrestricted}
                                                    inputAttr={removed ? { style: "text-decoration: line-through" } : undefined}
                                                />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <TextBox
                                                    value={row.rename}
                                                    onValueChanged={(e) => updateRow(row.key, { rename: e.value })}
                                                    placeholder="Keep uploaded name"
                                                    width="100%"
                                                    // Fixed once the document has been saved: files already
                                                    // uploaded are stored under this name, so renaming it
                                                    // here would leave them behind.
                                                    disabled={removed || unrestricted || !staged.isAdded(row.key)}
                                                    hint={staged.isAdded(row.key) ? undefined : "Set when the document was added"}
                                                />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <TagBox
                                                    items={COMMON_EXTENSIONS}
                                                    value={row.extensions}
                                                    // Take the box's value as it comes and only when it actually
                                                    // differs. Normalizing here (or writing back an array with the
                                                    // same contents) hands the box a value it does not recognize as
                                                    // its own, and the two re-trigger each other until React gives
                                                    // up. Normalizing happens on the way in and on save instead.
                                                    onValueChanged={(e) => {
                                                        const next: string[] = e.value ?? [];
                                                        if (sameExtensions(next, row.extensions)) return;
                                                        updateRow(row.key, { extensions: next });
                                                    }}
                                                    // Checkboxes keep the list open, so several
                                                    // types are one visit rather than one each. The
                                                    // Select All they bring is hidden by the CSS:
                                                    // ticking every type would read as a restriction
                                                    // to exactly those, when "any type" is what an
                                                    // empty box already means.
                                                    dropDownOptions={{ wrapperAttr: { class: "slot-types-dropdown" } }}
                                                    showSelectionControls={true}
                                                    applyValueMode="instantly"
                                                    hideSelectedItems={false}
                                                    searchEnabled={true}
                                                    acceptCustomValue={true}
                                                    onCustomItemCreating={(e) => {
                                                        const ext = normalizeExtension(e.text ?? "");
                                                        e.customItem = ext.length > 0 ? ext : null;
                                                    }}
                                                    placeholder="Any type"
                                                    width="100%"
                                                    disabled={removed || unrestricted}
                                                />
                                            </div>
                                            <div style={{ width: "40px", textAlign: "right" }}>
                                                <Button
                                                    icon={removed ? "undo" : "trash"}
                                                    stylingMode="text"
                                                    hint={removed ? "Keep document" : "Remove document"}
                                                    onClick={() => removed ? staged.restoreRow(row.key) : confirmRemoveRow(row)}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                                </Sortable>
                            </div>
                </TemplateGroupBox>
                    )}
                </div>
            </div>

            <div className="template-tab-footer">
                <Button
                    text={isDirty ? "Save *" : "Save"}
                    type="default"
                    stylingMode="contained"
                    disabled={errors.length > 0}
                    onClick={handleSave}
                />
                {isDirty && (
                    <Button text="Discard Changes" type="normal" stylingMode="outlined" onClick={handleDiscard} />
                )}
                <Button
                    text="Restore Defaults"
                    type="normal"
                    stylingMode="outlined"
                    onClick={handleRestoreDefaults}
                />
                {errors.length > 0 ? (
                    <span data-testid="slot-errors" style={{ ...removedTextStyle, textDecoration: "none", fontSize: "12px" }}>
                        {errors[0]}
                    </span>
                ) : isDirty && (
                    <span style={{ color: "#f0ad4e", fontSize: "12px" }}>Unsaved changes</span>
                )}
            </div>
        </div>
    );
}
